/* eslint-disable no-unused-vars */
import React, { useState, useRef } from 'react';
import { uploadFilesToLSQ, uploadPrescription } from './helper';
import { toast } from 'react-toastify';

export default function UploadFilesLSQ() {
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [isDragOver, setIsDragOver] = useState(false);
    const [uploadedLSQFiles, setUploadedLSQFiles] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef(null);

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const handleFileSelect = (files) => {
        const newFiles = Array.from(files).map((file, index) => ({
            id: Date.now() + index,
            file: file,
            name: file.name,
            size: file.size,
            type: file.type,
            uploadedAt: new Date().toLocaleString()
        }));

        setUploadedFiles(prevFiles => [...prevFiles, ...newFiles]);
    };

    const handleFileInputChange = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            handleFileSelect(e.target.files);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragOver(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragOver(false);

        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            handleFileSelect(e.dataTransfer.files);
        }
    };

    const removeFile = (fileId) => {
        setUploadedFiles(prevFiles => prevFiles.filter(file => file.id !== fileId));
    };

    const openFileDialog = () => {
        fileInputRef.current?.click();
    };

    const handleUploadFiles = async () => {
        if (uploadedFiles.length === 0) {
            toast.error("Please upload at least one file");
            return;
        }

        setIsUploading(true);

        const uploadResponse = await uploadPrescription(uploadedFiles.map((file) => file.file));

        if (!uploadResponse) {
            toast.error("Failed to upload files");
            setIsUploading(false);
            return;
        }

        const uploadedPresciptions = uploadResponse.map((fileUploaded) => {
            return fileUploaded.image || fileUploaded.pdf
        })

        const uploadLSQFileResponse = await uploadFilesToLSQ(uploadedPresciptions);
        setUploadedLSQFiles(uploadLSQFileResponse);

        setIsUploading(false);
    }

    const copyToClipboard = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            toast.success("Link copied to clipboard!");
        } catch (err) {
            // Fallback for older browsers
            const textArea = document.createElement("textarea");
            textArea.value = text;
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            try {
                document.execCommand('copy');
                toast.success("Link copied to clipboard!");
            } catch (err) {
                toast.error("Failed to copy link");
            }
            document.body.removeChild(textArea);
        }
    };

    return (
        <div className='p-5'>
            <p className='text-left text-4xl font-semibold mb-7'>Upload Files</p>

            {
                !uploadedLSQFiles && <>
                    {/* Upload Area */}
                    <div
                        className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors duration-200 ${isDragOver
                            ? 'border-blue-400 bg-blue-50'
                            : 'border-gray-300 hover:border-gray-400'
                            }`}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                    >
                        <div className='flex flex-col items-center gap-4'>
                            <svg
                                className='w-12 h-12 text-gray-400'
                                fill='none'
                                stroke='currentColor'
                                viewBox='0 0 24 24'
                            >
                                <path
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                    strokeWidth={2}
                                    d='M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12'
                                />
                            </svg>
                            <div>
                                <p className='text-lg font-medium text-gray-700 mb-2'>
                                    Drag and drop files here, or click to select files
                                </p>
                                <p className='text-sm text-gray-500'>
                                    Support for multiple file formats
                                </p>
                            </div>
                            <button
                                type='button'
                                onClick={openFileDialog}
                                className='px-6 py-3 bg-[#28a48b] text-white rounded-lg hover:bg-[#1f7a6b] transition-colors duration-200 font-medium text-white-50'
                            >
                                Choose Files
                            </button>
                        </div>
                    </div>

                    {/* Hidden File Input */}
                    <input
                        ref={fileInputRef}
                        type='file'
                        multiple
                        onChange={handleFileInputChange}
                        className='hidden'
                        accept='*/*'
                    />

                    {/* Uploaded Files List */}
                    {uploadedFiles.length > 0 && (
                        <div className='mt-8'>
                            <div className='flex justify-between items-center mb-4'>
                                <h3 className='text-xl font-semibold text-gray-700'>
                                    Uploaded Files ({uploadedFiles.length})
                                </h3>
                                <button
                                    type='button'
                                    onClick={openFileDialog}
                                    className='px-4 py-2 bg-[#28a48b] text-white rounded-lg hover:bg-[#1f7a6b] transition-colors duration-200 font-medium text-white-50'
                                >
                                    Add More Files
                                </button>
                            </div>

                            <div className='space-y-3'>
                                {uploadedFiles.map((fileItem) => (
                                    <div
                                        key={fileItem.id}
                                        className='flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200'
                                    >
                                        <div className='flex items-center gap-3'>
                                            <div className='w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center'>
                                                <svg
                                                    className='w-5 h-5 text-blue-600'
                                                    fill='none'
                                                    stroke='currentColor'
                                                    viewBox='0 0 24 24'
                                                >
                                                    <path
                                                        strokeLinecap='round'
                                                        strokeLinejoin='round'
                                                        strokeWidth={2}
                                                        d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
                                                    />
                                                </svg>
                                            </div>
                                            <div>
                                                <p className='font-medium text-gray-900'>{fileItem.name}</p>
                                                <p className='text-sm text-gray-500'>
                                                    {formatFileSize(fileItem.size)} • Uploaded {fileItem.uploadedAt}
                                                </p>
                                            </div>
                                        </div>
                                        <button
                                            type='button'
                                            onClick={() => removeFile(fileItem.id)}
                                            className='p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200'
                                            title='Remove file'
                                        >
                                            <svg
                                                className='w-5 h-5'
                                                fill='none'
                                                stroke='currentColor'
                                                viewBox='0 0 24 24'
                                            >
                                                <path
                                                    strokeLinecap='round'
                                                    strokeLinejoin='round'
                                                    strokeWidth={2}
                                                    d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16'
                                                />
                                            </svg>
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Action Buttons */}
                    {uploadedFiles.length > 0 && (
                        <div className='mt-6 flex gap-3'>
                            <button
                                type='button'
                                className='px-6 py-3 bg-[#28a48b] text-white rounded-lg hover:bg-[#1f7a6b] transition-colors duration-200 font-medium text-white-50 disabled:opacity-50 disabled:cursor-not-allowed'
                                onClick={handleUploadFiles}
                                disabled={isUploading}
                            >
                                {isUploading ? 'Uploading...' : 'Upload Files'}
                            </button>
                            <button
                                type='button'
                                onClick={() => setUploadedFiles([])}
                                className='px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors duration-200 font-medium text-white-50'
                            >
                                Clear All
                            </button>
                        </div>
                    )}
                </>
            }

            {/* Upload Success Section */}
            {uploadedLSQFiles && (
                <div className='mt-8 p-6 bg-green-50 border border-green-200 rounded-lg'>
                    <div className='flex items-center gap-2 mb-4'>
                        <svg
                            className='w-6 h-6 text-green-600'
                            fill='none'
                            stroke='currentColor'
                            viewBox='0 0 24 24'
                        >
                            <path
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                strokeWidth={2}
                                d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z'
                            />
                        </svg>
                        <h3 className='text-xl font-semibold text-green-800'>
                            Upload Successful!
                        </h3>
                    </div>

                    <div className='space-y-4'>
                        <div className='bg-white p-4 rounded-lg border border-green-100'>
                            <div className='flex items-center justify-between mb-2'>
                                <span className='text-sm font-medium text-gray-600'>File Name:</span>
                                <span className='text-sm text-gray-900 font-mono'>{uploadedLSQFiles.file}</span>
                            </div>

                            <div className='flex items-center justify-between mb-3'>
                                <span className='text-sm font-medium text-gray-600'>Download Link:</span>
                            </div>

                            <div className='flex items-center gap-2 p-3 bg-gray-50 rounded-lg border'>
                                <input
                                    type='text'
                                    value={uploadedLSQFiles.link}
                                    readOnly
                                    className='flex-1 bg-transparent text-sm text-gray-700 outline-none'
                                />
                                <button
                                    type='button'
                                    onClick={() => copyToClipboard(uploadedLSQFiles.link)}
                                    className='px-3 py-1.5 bg-[#28a48b] text-white rounded-md hover:bg-[#1f7a6b] transition-colors duration-200 text-sm font-medium'
                                    title='Copy link to clipboard'
                                >
                                    <svg
                                        className='w-4 h-4 text-white-50'
                                        fill='none'
                                        stroke='currentColor'
                                        viewBox='0 0 24 24'
                                    >
                                        <path
                                            strokeLinecap='round'
                                            strokeLinejoin='round'
                                            strokeWidth={2}
                                            d='M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z'
                                        />
                                    </svg>
                                </button>
                            </div>

                            <div className='mt-3 flex gap-2'>
                                <a
                                    href={uploadedLSQFiles.link}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className='px-4 py-2 bg-[#28a48b] text-white rounded-lg hover:bg-[#1f7a6b] transition-colors duration-200 text-sm font-medium text-white-50'
                                >
                                    Open File
                                </a>
                                <button
                                    type='button'
                                    onClick={() => {
                                        setUploadedLSQFiles(null);
                                        setUploadedFiles([]);
                                    }}
                                    className='px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors duration-200 text-sm font-medium text-white-50'
                                >
                                    Upload New Files
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
